import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom'; // Import Link for navigation
import {formatCurrency, formatRate} from "./CoreFunctions";

const PhysicalAssetsPage = () => {
    const [physicalAssets, setPhysicalAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('tiles'); // default to 'tiles'
    const [openDebtTip, setOpenDebtTip] = useState({ assetKey: null }); // controls which asset's debt tooltip is open
    const [debtAmtCache, setDebtAmtCache] = useState({}); // { [key: `${type}:${id}`]: amount }
    // Shared title column width for aligning metrics across headers
    const propertiesTitleRef = useRef(null);
    const vehiclesTitleRef = useRef(null);
    const [titleColWidth, setTitleColWidth] = useState(null);
    // Collapse state (tiles view)
    const [propertiesExpanded, setPropertiesExpanded] = useState(true);
    const [vehiclesExpanded, setVehiclesExpanded] = useState(true);
    // Period and delta mode state (prepared for future deltas wiring)
    const [period, setPeriod] = useState('YTD'); // '1M' | '3M' | '6M' | '12M' | 'YTD' | 'ALL'
    const [deltaMode, setDeltaMode] = useState('%'); // '%' | '$'

    const [marketHistory, setMarketHistory] = useState([]);
    const [debtHistory, setDebtHistory] = useState([]);
    const [tcoCache, setTcoCache] = useState({}); // { [assetId]: { [period]: total } }
    const currentMonthNum = 5; // June 15, 2026 -> 5 months of loaded data (Jan-May)

    useEffect(() => {
        const fetchAssets = async () => {
            try {
                const response = await axios.get('/api/balance_sheet/assets/physical_assets/');
                setPhysicalAssets(response.data);
                // After fetching assets, start loading TCO for each
                response.data.forEach(asset => loadTCOForAsset(asset.id, asset.asset_type_2));
            } catch (error) {
                console.error("Error fetching assets:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchAssets();
    }, []);

    const loadTCOForAsset = async (assetId, assetType) => {
        try {
            const now = new Date();
            const currentYear = now.getFullYear();
            const jan1Current = `${currentYear}-01-01`;
            const dec31Prior = `${currentYear - 1}-12-31`;
            const jan1Prior = `${currentYear - 1}-01-01`;

            const getPastDate = (months) => {
                const d = new Date();
                d.setMonth(d.getMonth() - months);
                return d.toISOString().slice(0, 10);
            };

            const fetchSum = async (start, end) => {
                const params = {
                    only_txn_backed: 1,
                    include_asset_input: assetType === 'vehicle' ? 1 : 0,
                    include_children: assetType === 'resi_property' ? 1 : 0,
                };
                if (start) params.start_date = start;
                if (end) params.end_date = end;

                const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${assetId}/linked-expenses/`, { params });
                const actuals = res.data?.actual || [];
                
                // TCO logic matching detail pages
                const opFiltered = actuals.filter(item => item.expense_group === 'Operating');
                const finFiltered = actuals.filter(item => item.expense_group === 'Financing');
                const capexFiltered = actuals.filter(item => item.expense_group === 'CapEx' && item.expense_category !== 'initial_purchase');
                
                const total = opFiltered.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0) +
                              finFiltered.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0) +
                              capexFiltered.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0);
                
                return total;
            };

            const [m1, m3, m6, m12, ytd, all] = await Promise.all([
                fetchSum(getPastDate(1), null),
                fetchSum(getPastDate(3), null),
                fetchSum(getPastDate(6), null),
                fetchSum(getPastDate(12), null),
                fetchSum(jan1Current, null),
                fetchSum(null, null)
            ]);

            setTcoCache(prev => ({
                ...prev,
                [assetId]: { '1M': m1, '3M': m3, '6M': m6, '12M': m12, 'YTD': ytd, 'ALL': all }
            }));
        } catch (e) {
            console.error(`Failed to load TCO for asset ${assetId}`, e);
        }
    };

    // Fetch historical series for deltas
    useEffect(() => {
        const fetchHistories = async () => {
            try {
                const [mRes, dRes] = await Promise.all([
                    axios.get('/api/balance_sheet/assets/physical_assets/market_values/history/'),
                    axios.get('/api/balance_sheet/assets/physical_assets/debt_values/history/'),
                ]);
                setMarketHistory(mRes.data);
                setDebtHistory(dRes.data);
            } catch (e) {
                console.error("Error fetching histories for deltas:", e);
            }
        };
        fetchHistories();
    }, []);

    // --- Delta Computations ---
    const getDelta = (assetId, metric) => {
        // Find series for this asset
        const mHist = marketHistory.find(h => h.id === assetId)?.series || [];
        const dHist = debtHistory.find(h => h.id === assetId)?.series || [];

        if (mHist.length === 0 && dHist.length === 0) return null;

        // Current value: usually last in series or fall back to current
        const getValAt = (series, dateStr) => {
            if (!series || series.length === 0) return 0;
            // series is [{date, value}] sorted by date
            const found = series.find(p => p.date === dateStr);
            if (found) return found.value;
            // fallback: find latest point <= dateStr
            const pts = series.filter(p => p.date <= dateStr);
            if (pts.length > 0) return pts[pts.length-1].value;
            return 0;
        };

        const now = new Date();
        const ym = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
        const currentMonth = ym(now);

        const subtractMonths = (ymStr, n) => {
            const [yy, mm] = ymStr.split('-').map(Number);
            const d = new Date(yy, mm - 1, 1);
            d.setMonth(d.getMonth() - n);
            return ym(d);
        };

        let baselineMonth = currentMonth;
        switch (period) {
            case '1M': baselineMonth = subtractMonths(currentMonth, 1); break;
            case '3M': baselineMonth = subtractMonths(currentMonth, 3); break;
            case '6M': baselineMonth = subtractMonths(currentMonth, 6); break;
            case '12M': baselineMonth = subtractMonths(currentMonth, 12); break;
            case 'YTD': {
                const y = now.getFullYear();
                baselineMonth = `${y-1}-12`;
                break;
            }
            case 'ALL': {
                const allDates = [...mHist.map(p => p.date), ...dHist.map(p => p.date)].sort();
                baselineMonth = allDates[0] || currentMonth;
                break;
            }
            default: baselineMonth = currentMonth;
        }

        const getMetricVal = (month) => {
            const v = getValAt(mHist, month);
            const d = getValAt(dHist, month);
            if (metric === 'value') return v;
            if (metric === 'debt') return d;
            if (metric === 'equity') return v - d;
            return 0;
        };

        const currentVal = getMetricVal(currentMonth);
        const baselineVal = getMetricVal(baselineMonth);
        const diff = currentVal - baselineVal;

        if (deltaMode === '$') {
            const absDiff = Math.abs(diff);
            const sign = diff > 0 ? '+' : (diff < 0 ? '-' : '');
            return `${sign}${formatCurrency(absDiff, 0)}`;
        } else {
            if (baselineVal === 0) return diff === 0 ? '0.0%' : '∞%';
            const pct = (diff / baselineVal) * 100;
            const sign = pct > 0 ? '+' : (pct < 0 ? '-' : '');
            return `${sign}${Math.abs(pct).toFixed(1)}%`;
        }
    };

    const getGroupDelta = (assets, metric) => {
        if (!assets || assets.length === 0) return null;

        const now = new Date();
        const ym = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
        const currentMonth = ym(now);

        const subtractMonths = (ymStr, n) => {
            const [yy, mm] = ymStr.split('-').map(Number);
            const d = new Date(yy, mm - 1, 1);
            d.setMonth(d.getMonth() - n);
            return ym(d);
        };

        let baselineMonth = currentMonth;
        switch (period) {
            case '1M': baselineMonth = subtractMonths(currentMonth, 1); break;
            case '3M': baselineMonth = subtractMonths(currentMonth, 3); break;
            case '6M': baselineMonth = subtractMonths(currentMonth, 6); break;
            case '12M': baselineMonth = subtractMonths(currentMonth, 12); break;
            case 'YTD': {
                const y = now.getFullYear();
                baselineMonth = `${y-1}-12`;
                break;
            }
            case 'ALL': baselineMonth = '1900-01'; break; // Placeholder, will find min
            default: baselineMonth = currentMonth;
        }

        let totalCurrent = 0;
        let totalBaseline = 0;

        let earliestFound = '9999-12';

        assets.forEach(a => {
            const mHist = marketHistory.find(h => h.id === a.id)?.series || [];
            const dHist = debtHistory.find(h => h.id === a.id)?.series || [];

            if (period === 'ALL') {
                const allDates = [...mHist.map(p => p.date), ...dHist.map(p => p.date)].sort();
                if (allDates.length > 0 && allDates[0] < earliestFound) earliestFound = allDates[0];
            }

            const getValAt = (series, dateStr) => {
                if (!series || series.length === 0) return 0;
                const found = series.find(p => p.date === dateStr);
                if (found) return found.value;
                const pts = series.filter(p => p.date <= dateStr);
                if (pts.length > 0) return pts[pts.length-1].value;
                return 0;
            };

            const getMetricVal = (month) => {
                const v = getValAt(mHist, month);
                const d = getValAt(dHist, month);
                if (metric === 'value') return v;
                if (metric === 'debt') return d;
                if (metric === 'equity') return v - d;
                return 0;
            };

            totalCurrent += getMetricVal(currentMonth);
            // We'll calculate totalBaseline after loop if period is ALL
            if (period !== 'ALL') {
                totalBaseline += getMetricVal(baselineMonth);
            }
        });

        if (period === 'ALL') {
            baselineMonth = earliestFound === '9999-12' ? currentMonth : earliestFound;
            assets.forEach(a => {
                const mHist = marketHistory.find(h => h.id === a.id)?.series || [];
                const dHist = debtHistory.find(h => h.id === a.id)?.series || [];
                const getValAt = (series, dateStr) => {
                    if (!series || series.length === 0) return 0;
                    const found = series.find(p => p.date === dateStr);
                    if (found) return found.value;
                    const pts = series.filter(p => p.date <= dateStr);
                    if (pts.length > 0) return pts[pts.length-1].value;
                    return 0;
                };
                const v = getValAt(mHist, baselineMonth);
                const d = getValAt(dHist, baselineMonth);
                if (metric === 'value') totalBaseline += v;
                else if (metric === 'debt') totalBaseline += d;
                else if (metric === 'equity') totalBaseline += (v - d);
            });
        }

        const diff = totalCurrent - totalBaseline;

        if (deltaMode === '$') {
            const absDiff = Math.abs(diff);
            const sign = diff > 0 ? '+' : (diff < 0 ? '-' : '');
            return `${sign}${formatCurrency(absDiff, 0)}`;
        } else {
            if (totalBaseline === 0) return diff === 0 ? '0.0%' : '∞%';
            const pct = (diff / totalBaseline) * 100;
            const sign = pct > 0 ? '+' : (pct < 0 ? '-' : '');
            return `${sign}${Math.abs(pct).toFixed(1)}%`;
        }
    };

    const getDeltaColor = (deltaStr, metric) => {
        if (!deltaStr) return 'inherit';
        const isNegative = deltaStr.includes('-');
        const isZero = deltaStr.includes('0.0%') || deltaStr.includes('$0');

        if (isZero) return '#9ca3af'; // gray-400

        if (metric === 'value' || metric === 'equity') {
            return isNegative ? '#ef4444' : '#10b981'; // red-500 : green-500
        }
        if (metric === 'debt') {
            return isNegative ? '#10b981' : '#ef4444'; // green-500 : red-500 (debt decrease is good)
        }
        return 'inherit';
    };

    // --- End Delta Computations ---

    // After assets load, opportunistically prefetch current outstanding per linked debts
    useEffect(() => {
        const prefetch = async () => {
            try {
                const list = Array.isArray(physicalAssets) ? physicalAssets : [];
                // Build unique debt id + type targets across all assets (include mortgages/auto from financing and HELCOs from other_linked_debt)
                const targets = [];
                for (const a of list) {
                    const detailed = a?.financing?.debt_financing_detailed;
                    const rows = Array.isArray(detailed) ? detailed : (detailed && typeof detailed === 'object' ? Object.values(detailed) : []);
                    for (const it of rows) {
                        if (!it) continue;
                        const id = it.id ?? it.pk ?? it.loan_id;
                        if (id == null) continue;
                        const typeHint = String(it.instrument_type || it.type || it.debt_type || it.product || it.category || '').toLowerCase();
                        let api = null;
                        let debtType = null;
                        if (typeHint.includes('mortgage')) { debtType = 'mortgage'; api = `/api/balance_sheet/financing/mortgages/${id}/`; }
                        else if (typeHint.includes('heloc') || typeHint.includes('revolver') || typeHint.includes('line of credit') || typeHint.includes('loc')) { debtType = 'heloc'; api = `/api/balance_sheet/financing/revolvers/helocs/${id}/`; }
                        else if (typeHint.includes('auto') || typeHint.includes('vehicle') || a?.asset_type_2 === 'vehicle') { debtType = 'auto'; api = `/api/balance_sheet/financing/auto_loans/${id}/`; }
                        // Default: assume mortgage for properties
                        if (!api) {
                            if (a?.asset_type_2 === 'vehicle') { debtType = 'auto'; api = `/api/balance_sheet/financing/auto_loans/${id}/`; }
                            else { debtType = 'mortgage'; api = `/api/balance_sheet/financing/mortgages/${id}/`; }
                        }
                        targets.push({ id, api, debtType });
                    }

                    // Also include HELOCs exposed under other_linked_debt for properties
                    const helocs = a?.other_linked_debt?.helocs;
                    const hlist = Array.isArray(helocs) ? helocs : [];
                    for (const h of hlist) {
                        if (!h) continue;
                        const hid = h.id ?? h.pk ?? h.loan_id;
                        if (hid == null) continue;
                        const inlineBal = (h.current_balance != null) ? Number(h.current_balance) : null;
                        // If inline balance exists, seed cache directly
                        if (inlineBal !== null && !isNaN(inlineBal)) {
                            const key = `heloc:${hid}`;
                            setDebtAmtCache(prev => (prev[key] === inlineBal ? prev : { ...prev, [key]: inlineBal }));
                        }
                        targets.push({ id: hid, api: `/api/balance_sheet/financing/revolvers/helocs/${hid}/`, debtType: 'heloc' });
                    }
                }
                // Deduplicate by id
                const seen = new Set();
                const unique = targets.filter(t => {
                    const k = `${t.debtType || 'mortgage'}:${t.id}`;
                    if (seen.has(k)) return false; seen.add(k); return true;
                });
                // Fetch sequentially in small batches to avoid spikes
                for (const t of unique) {
                    const key = `${t.debtType || 'mortgage'}:${t.id}`;
                    if (debtAmtCache[key] != null) continue;
                    try {
                        const res = await axios.get(t.api);
                        const d = res?.data || {};
                        // Robust extraction across models/serializers
                        let amt = d.current_outstanding ?? d.current_balance ?? d.current_outstanding_debt ?? d.outstanding;
                        // For mortgages, outstanding can be nested under upcoming/recent payment
                        if ((amt == null || amt === 0) && d.upcoming_payment && d.upcoming_payment.outstanding_balance != null) {
                            amt = d.upcoming_payment.outstanding_balance;
                        }
                        if ((amt == null || amt === 0) && d.recent_payment && d.recent_payment.outstanding_balance != null) {
                            amt = d.recent_payment.outstanding_balance;
                        }
                        // djmoney shapes
                        if (amt && typeof amt === 'object' && amt.amount != null) amt = amt.amount;
                        const num = Number(amt) || 0;
                        setDebtAmtCache(prev => (prev[key] === num ? prev : { ...prev, [key]: num }));
                    } catch (e) {
                        // ignore individual failures
                    }
                }
            } catch (e) { /* ignore */ }
        };
        if (!loading && physicalAssets && physicalAssets.length) {
            prefetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, physicalAssets]);

    // Measure title widths and set a shared title column width so metrics start at the same x-position
    useEffect(() => {
        if (viewMode !== 'tiles') return;
        const compute = () => {
            try {
                const els = [propertiesTitleRef.current, vehiclesTitleRef.current].filter(Boolean);
                if (!els.length) return;
                const widths = els.map(el => (el && el.offsetWidth) ? el.offsetWidth : 0);
                const max = widths.length ? Math.max(...widths) : 0;
                const pad = 24; // fixed padding so metrics sit with a clear gap
                const next = Math.max(0, Math.ceil(max + pad));
                setTitleColWidth(prev => (prev === next ? prev : next));
            } catch (_) { /* ignore */ }
        };
        const timer = setTimeout(compute, 0); // defer to ensure layout settled
        window.addEventListener('resize', compute);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', compute);
        };
    }, [viewMode, loading, physicalAssets]);

    // Keyboard shortcuts for collapsing/expanding (only in tiles view)
    useEffect(() => {
        if (viewMode !== 'tiles') return;
        const onKey = (e) => {
            // Ignore when typing in inputs/textareas or when modifier keys pressed
            const tag = (e.target && e.target.tagName) ? e.target.tagName.toLowerCase() : '';
            if (tag === 'input' || tag === 'textarea' || e.ctrlKey || e.metaKey || e.altKey) return;
            if (e.key === 'p' || e.key === 'P') {
                setPropertiesExpanded(prev => !prev);
            } else if (e.key === 'v' || e.key === 'V') {
                setVehiclesExpanded(prev => !prev);
            } else if (e.key === 'a' || e.key === 'A') {
                // Toggle both based on whether both are currently expanded
                const bothExpanded = propertiesExpanded && vehiclesExpanded;
                setPropertiesExpanded(!bothExpanded);
                setVehiclesExpanded(!bothExpanded);
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [viewMode, propertiesExpanded, vehiclesExpanded]);

    // Close tooltip on outside click or ESC
    useEffect(() => {
        const onDocClick = () => setOpenDebtTip({ assetKey: null });
        const onKey = (e) => { if (e.key === 'Escape') setOpenDebtTip({ assetKey: null }); };
        document.addEventListener('click', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('click', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, []);

    if (loading) {
        return <div>Loading assets...</div>;
    }

    // Grouping assets (assumption: each asset may have a structure or land)
    const categorizedAssets = physicalAssets.reduce((acc, asset) => {
        if (asset.asset_type_2 === 'resi_property') {
            acc.residentialProperties.push(asset);
        } else if (asset.asset_type_2 === 'structure') {
            acc.structures.push(asset);
        } else if (asset.asset_type_2 === 'land') {
            acc.lands.push(asset);
        } else if (asset.asset_type_2 === 'vehicle') {
            acc.vehicles.push(asset);
        }
        return acc;
    }, { residentialProperties: [], structures: [], lands: [], vehicles: [] });


    // Helper function to safely access nested properties:
    const getNestedValue = (obj, path) => {
      const value = path.reduce((acc, key) => (acc && acc[key] !== undefined) ? acc[key] : 0, obj);
      return typeof value === 'number' ? value : Number(value) || 0;
    };

    // Define the nested paths for each metric:
    const metricsPaths = {
        initialPurchasePrice: ['initial_purchase_price', 'amount'],
        totalEquity: ['financing', 'total_equity_amount'],
        totalDebt: ['financing', 'total_debt_amount'],
        totalCurrentBvEquity: ['current_bv_equity'],
        totalCurrentOsDebt: ['current_outstanding_debt'],
    };

    // Initialize totals:
    const totalFields = {
        initialPurchasePrice: 0,
        totalEquity: 0,
        totalDebt: 0,
        totalCurrentBvEquity: 0,
        totalCurrentOsDebt: 0,
        totalTCO: 0,
    };

    // Loop through each category and asset to sum metrics:
    Object.keys(categorizedAssets).forEach(category => {
        categorizedAssets[category].forEach(asset => {
            totalFields.initialPurchasePrice += getNestedValue(asset, metricsPaths.initialPurchasePrice);
            totalFields.totalEquity += getNestedValue(asset, metricsPaths.totalEquity);
            totalFields.totalDebt += getNestedValue(asset, metricsPaths.totalDebt);
            totalFields.totalCurrentBvEquity += getNestedValue(asset, metricsPaths.totalCurrentBvEquity);
            totalFields.totalCurrentOsDebt += getNestedValue(asset, metricsPaths.totalCurrentOsDebt);
            totalFields.totalTCO += (tcoCache[asset.id]?.[period] || 0);
        });
    });

    // Assuming 'categorizedAssets.residentialProperties' is your array of properties
    categorizedAssets.residentialProperties.forEach(resProperty => {
        // Access the 'financing' attribute
        const financing = resProperty.financing;

        // Check if 'financing' exists and has 'debt_financing'
        if (financing && financing.debt_financing && Array.isArray(financing.debt_financing)) {
            // Loop through each debt object
            financing.debt_financing.forEach(debt => {
                // Do something with each debt object
                console.log(debt.current_outstanding);
            });
        }
    });

    // Helper: render icon by type
    const renderIcon = (asset) => {
        // Use simple emoji icons to avoid bundling image assets
        if (asset._tileType === 'vehicle' || asset.asset_type_2 === 'vehicle') return '🚗';
        if (asset._tileType === 'resi_property' || asset.asset_type_2 === 'resi_property') {
            // Prefer backend-provided hint for properties
            if (asset.tile_kind === 'land') return '🌿';
            // default to house when structures exist or unknown
            return '🏠';
        }
        if (asset.asset_type_2 === 'land') return '🌿';
        return '📦';
    };

    // Compute a "current value" for sorting: BV equity + outstanding debt, fallback to initial purchase price
    const calcCurrentValue = (a) => {
        const equity = Number(a?.current_bv_equity ?? a?.financing?.total_equity_amount ?? 0) || 0;
        const debt = Number(a?.current_outstanding_debt ?? a?.financing?.total_debt_amount ?? 0) || 0;
        const bv = equity + debt;
        if (bv > 0) return bv;
        return Number(a?.initial_purchase_price?.amount ?? 0) || 0;
    };

    // Sorted groups for tiles view
    const sortedProperties = [...categorizedAssets.residentialProperties].sort((a, b) => calcCurrentValue(b) - calcCurrentValue(a));
    const sortedVehicles = [...categorizedAssets.vehicles].sort((a, b) => calcCurrentValue(b) - calcCurrentValue(a));

    // Helpers to aggregate header metrics for a group
    const sumPurchaseAmount = (list) => {
        try {
            let s = 0;
            for (const a of list) {
                const v = a?.initial_purchase_price?.amount;
                if (v != null && !isNaN(Number(v))) s += Number(v);
            }
            return s;
        } catch (e) { return 0; }
    };
    const sumCurrentValue = (list) => {
        try { return list.reduce((acc, a) => acc + (Number(getCurrentValue(a)) || 0), 0); } catch { return 0; }
    };
    const sumOutstandingAll = (list) => {
        try { return list.reduce((acc, a) => acc + (Number(sumOutstandingDebt(a)) || 0), 0); } catch { return 0; }
    };
    const sumGroupTCO = (list) => {
        try { return list.reduce((acc, a) => acc + (Number(tcoCache[a.id]?.[period]) || 0), 0); } catch { return 0; }
    };
    const buildGroupTotals = (list) => {
        const value = sumCurrentValue(list);
        const debt = sumOutstandingAll(list);
        const equity = value - debt;
        const purchase = sumPurchaseAmount(list);
        const tco = sumGroupTCO(list);
        return { value, debt, equity, purchase, tco };
    };

    const HeaderMetrics = ({ totals, groupAssets, isTicker }) => {
        const eqCls = (totals.equity >= 0) ? 'metric-value positive' : 'metric-value negative';

        const dEquity = groupAssets ? getGroupDelta(groupAssets, 'equity') : null;
        const dValue = groupAssets ? getGroupDelta(groupAssets, 'value') : null;
        const dDebt = groupAssets ? getGroupDelta(groupAssets, 'debt') : null;

        const metricsStyle = isTicker 
            ? { display:'flex', alignItems:'baseline' } 
            : { display:'grid', gridTemplateColumns: 'repeat(5, 180px)', gap:16, alignItems:'baseline' };

        return (
            <div className={`header-metrics ${isTicker ? 'ticker' : ''}`} style={metricsStyle}>
                {/* Order: Current Equity, Current Value, Outstanding Debt, TCO, Purchase Amount */}
                <div className="header-metric">
                    <span className="metric-label">{isTicker ? 'Equity' : 'Current Equity'}</span>{' '}
                    <span className={eqCls}>
                        {formatCurrency(totals.equity || 0, 0)}
                        {dEquity && (
                            <span style={{ fontSize: '0.85em', marginLeft: 4, color: getDeltaColor(dEquity, 'equity') }}>
                                ({dEquity})
                            </span>
                        )}
                    </span>
                </div>
                <div className="header-metric">
                    <span className="metric-label">{isTicker ? 'Value' : 'Current Value'}</span>{' '}
                    <span className="metric-value">
                        {formatCurrency(totals.value || 0, 0)}
                        {dValue && (
                            <span style={{ fontSize: '0.85em', marginLeft: 4, color: getDeltaColor(dValue, 'value') }}>
                                ({dValue})
                            </span>
                        )}
                    </span>
                </div>
                <div className="header-metric">
                    <span className="metric-label">{isTicker ? 'Debt' : 'Outstanding Debt'}</span>{' '}
                    <span className="metric-value">
                        {formatCurrency(totals.debt || 0, 0)}
                        {dDebt && (
                            <span style={{ fontSize: '0.85em', marginLeft: 4, color: getDeltaColor(dDebt, 'debt') }}>
                                ({dDebt})
                            </span>
                        )}
                    </span>
                </div>
                <div className="header-metric">
                    <span className="metric-label">TCO ({period})</span>{' '}
                    <span className="metric-value" style={{ color: '#fbbf24' }}>
                        {(() => {
                            const val = totals.tco || 0;
                            if (period === 'YTD') {
                                const avg = val / currentMonthNum;
                                return `${formatCurrency(val, 0)} / ${formatCurrency(avg, 0)}`;
                            }
                            return formatCurrency(val, 0);
                        })()}
                    </span>
                </div>
                {!isTicker && (
                    <div className="header-metric">
                        <span className="metric-label">Purchase Amount</span>{' '}
                        <span className="metric-value">{formatCurrency(totals.purchase || 0, 0)}</span>
                    </div>
                )}
            </div>
        );
    };

    // Build per-debt breakdown for tooltip with links (uses cached amounts when available)
    const buildDebtBreakdown = (asset) => {
        try {
            const out = [];
            const detailed = asset?.financing?.debt_financing_detailed;
            const list = Array.isArray(detailed) ? detailed : (typeof detailed === 'object' ? Object.values(detailed) : []);
            for (const it of list) {
                const id = it?.id ?? it?.pk ?? it?.loan_id ?? null;
                // Determine type and key
                const rawType = String(it?.instrument_type || it?.type || it?.debt_type || it?.product || it?.category || '').toLowerCase();
                const isVehicle = asset?.asset_type_2 === 'vehicle';
                let label = 'Debt';
                let href = '#';
                let key = null;
                if (rawType.includes('mortgage')) {
                    label = 'Mortgage';
                    href = id != null ? `/balance_sheet/financing/mortgages/${id}` : '#';
                    key = id != null ? `mortgage:${id}` : null;
                } else if (rawType.includes('heloc') || rawType.includes('revolver') || rawType.includes('line of credit') || rawType.includes('loc')) {
                    label = 'HELOC';
                    href = id != null ? `/balance_sheet/financing/revolvers/helocs/${id}` : '#';
                    key = id != null ? `heloc:${id}` : null;
                } else if (rawType.includes('auto') || rawType.includes('vehicle') || isVehicle) {
                    label = 'Auto Loan';
                    href = id != null ? `/balance_sheet/financing/auto_loans/${id}` : '#';
                    key = id != null ? `auto:${id}` : null;
                } else {
                    // Fallback by asset type
                    if (isVehicle) {
                        label = 'Auto Loan';
                        href = id != null ? `/balance_sheet/financing/auto_loans/${id}` : '#';
                        key = id != null ? `auto:${id}` : null;
                    } else {
                        label = 'Mortgage';
                        href = id != null ? `/balance_sheet/financing/mortgages/${id}` : '#';
                        key = id != null ? `mortgage:${id}` : null;
                    }
                }
                // Prefer cached fetched amount; for HELOC also fallback to inline current_balance when available
                let amt = undefined;
                if (key && Object.prototype.hasOwnProperty.call(debtAmtCache, key)) {
                    amt = debtAmtCache[key];
                }
                // If this specific row is a HELOC and not in cache, try to find inline value from other_linked_debt
                if ((amt === undefined || Number.isNaN(amt)) && key && key.startsWith('heloc:')) {
                    try {
                        const hid = id;
                        const helocs = asset?.other_linked_debt?.helocs;
                        const hlist = Array.isArray(helocs) ? helocs : [];
                        const found = hlist.find(h => (h?.id ?? h?.pk ?? h?.loan_id) === hid);
                        if (found && found.current_balance != null) {
                            const inlineNum = Number(found.current_balance);
                            if (!isNaN(inlineNum)) amt = inlineNum;
                        }
                    } catch (_) { /* ignore */ }
                }
                out.push({ id, key, amount: (typeof amt === 'number') ? amt : 0, label, href });
            }

            // Include HELOCs from other_linked_debt (for properties)
            const helocs = asset?.other_linked_debt?.helocs;
            const hlist = Array.isArray(helocs) ? helocs : [];
            for (const h of hlist) {
                const id = h?.id ?? h?.pk ?? h?.loan_id ?? null;
                if (id == null) continue;
                const key = `heloc:${id}`;
                const inline = (h?.current_balance != null) ? Number(h.current_balance) : null;
                const cached = (debtAmtCache[key] != null) ? Number(debtAmtCache[key]) : (inline != null ? inline : undefined);
                // Avoid duplicate if already present from financing list
                if (!out.some(r => r.key === key)) {
                    out.push({ id, key, amount: (typeof cached === 'number' && !isNaN(cached)) ? cached : 0, label: 'HELOC', href: `/balance_sheet/financing/revolvers/helocs/${id}` });
                }
            }

            return out;
        } catch (e) {
            return [];
        }
    };

    // Ensure debt amounts are loaded when opening the tooltip
    const onToggleDebtTip = async (assetKey, asset) => {
        setOpenDebtTip(prev => ({ assetKey: prev.assetKey === assetKey ? null : assetKey }));
        // If opening, fetch amounts for this asset's debts if not cached
        try {
            const detailed = asset?.financing?.debt_financing_detailed;
            const rows = Array.isArray(detailed) ? detailed : (detailed && typeof detailed === 'object' ? Object.values(detailed) : []);
            for (const it of rows) {
                if (!it) continue;
                const id = it.id ?? it.pk ?? it.loan_id;
                const rawType = String(it?.instrument_type || it?.type || it?.debt_type || it?.product || it?.category || '').toLowerCase();
                let api = null; let debtType = null;
                if (rawType.includes('mortgage')) { debtType = 'mortgage'; api = `/api/balance_sheet/financing/mortgages/${id}/`; }
                else if (rawType.includes('heloc') || rawType.includes('revolver') || rawType.includes('line of credit') || rawType.includes('loc')) { debtType = 'heloc'; api = `/api/balance_sheet/financing/revolvers/helocs/${id}/`; }
                else if (rawType.includes('auto') || rawType.includes('vehicle') || asset?.asset_type_2 === 'vehicle') { debtType = 'auto'; api = `/api/balance_sheet/financing/auto_loans/${id}/`; }
                const key = (id != null) ? `${debtType || (asset?.asset_type_2==='vehicle'?'auto':'mortgage')}:${id}` : null;
                if (id == null || (key && debtAmtCache[key] != null)) continue;
                if (!api) api = (asset?.asset_type_2 === 'vehicle')
                    ? `/api/balance_sheet/financing/auto_loans/${id}/`
                    : `/api/balance_sheet/financing/mortgages/${id}/`;
                try {
                    const res = await axios.get(api);
                    const d = res?.data || {};
                    let amt = d.current_outstanding ?? d.current_balance ?? d.current_outstanding_debt ?? d.outstanding;
                    if ((amt == null || amt === 0) && d.upcoming_payment && d.upcoming_payment.outstanding_balance != null) {
                        amt = d.upcoming_payment.outstanding_balance;
                    }
                    if ((amt == null || amt === 0) && d.recent_payment && d.recent_payment.outstanding_balance != null) {
                        amt = d.recent_payment.outstanding_balance;
                    }
                    if (amt && typeof amt === 'object' && amt.amount != null) amt = amt.amount;
                    const num = Number(amt) || 0;
                    if (key) setDebtAmtCache(prev => (prev[key] === num ? prev : { ...prev, [key]: num }));
                } catch (e) { /* ignore */ }
            }
            // Ensure HELOCs from other_linked_debt are fetched if not cached and no inline amount
            const helocs = asset?.other_linked_debt?.helocs;
            const hlist = Array.isArray(helocs) ? helocs : [];
            for (const h of hlist) {
                const id = h?.id;
                if (id == null) continue;
                const key = `heloc:${id}`;
                const inline = (h?.current_balance != null) ? Number(h.current_balance) : null;
                if (debtAmtCache[key] != null || (inline !== null && !isNaN(inline))) continue;
                try {
                    const res = await axios.get(`/api/balance_sheet/financing/revolvers/helocs/${id}/`);
                    const d = res?.data || {};
                    let amt = d.current_outstanding ?? d.current_balance ?? d.current_outstanding_debt ?? d.outstanding;
                    if ((amt == null || amt === 0) && d.upcoming_payment && d.upcoming_payment.outstanding_balance != null) {
                        amt = d.upcoming_payment.outstanding_balance;
                    }
                    if ((amt == null || amt === 0) && d.recent_payment && d.recent_payment.outstanding_balance != null) {
                        amt = d.recent_payment.outstanding_balance;
                    }
                    if (amt && typeof amt === 'object' && amt.amount != null) amt = amt.amount;
                    const num = Number(amt) || 0;
                    setDebtAmtCache(prev => (prev[key] === num ? prev : { ...prev, [key]: num }));
                } catch (e) { /* ignore */ }
            }
        } catch (e) { /* ignore */ }
    };

    // Sum all outstanding linked debts when possible; fallback to aggregate field
    const sumOutstandingDebt = (asset) => {
        try {
            const detailed = asset?.financing?.debt_financing_detailed;
            const rows = Array.isArray(detailed) ? detailed : (detailed && typeof detailed === 'object' ? Object.values(detailed) : []);
            const helocs = asset?.other_linked_debt?.helocs;
            const hlist = Array.isArray(helocs) ? helocs : [];
            if (!rows.length && !hlist.length) return Number(asset?.current_outstanding_debt || 0) || 0;
            let sum = 0;
            let anyMissing = false;
            for (const it of rows) {
                const id = it && (it.id ?? it.pk ?? it.loan_id);
                const rawType = String(it?.instrument_type || it?.type || it?.debt_type || it?.product || it?.category || '').toLowerCase();
                let key = null;
                if (rawType.includes('heloc') || rawType.includes('revolver') || rawType.includes('line of credit') || rawType.includes('loc')) key = id != null ? `heloc:${id}` : null;
                else if (asset?.asset_type_2 === 'vehicle' || rawType.includes('auto') || rawType.includes('vehicle')) key = id != null ? `auto:${id}` : null;
                else key = id != null ? `mortgage:${id}` : null;
                const cached = key ? debtAmtCache[key] : undefined;
                if (typeof cached === 'number') sum += cached; else anyMissing = true;
            }
            for (const h of hlist) {
                const id = h?.id;
                if (id == null) continue;
                const key = `heloc:${id}`;
                const inline = (h?.current_balance != null) ? Number(h.current_balance) : null;
                if (debtAmtCache[key] != null) sum += Number(debtAmtCache[key]);
                else if (inline !== null && !isNaN(inline)) sum += inline;
                else anyMissing = true;
            }
            // If we are missing some, include base as a fallback to avoid showing 0; otherwise use sum
            if (anyMissing && sum === 0) {
                return Number(asset?.current_outstanding_debt || 0) || 0;
            }
            return sum;
        } catch (e) {
            return Number(asset?.current_outstanding_debt || 0) || 0;
        }
    };

    const getCurrentValue = (asset) => {
        const cv = Number(asset?.current_value);
        if (!isNaN(cv) && isFinite(cv) && cv > 0) return cv;
        return calcCurrentValue(asset);
    };

return (
        <div className="assets-page">
            <div className="assets-page-content">
                <div className="page-header-row" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 8, marginBottom: '40px', width: '100%', justifyContent: 'flex-start', minWidth: 0 }}>
                    <div className="header-title-container" style={{ flexShrink: 1, minWidth: 0 }}>
                        <h1 style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Physical Assets</h1>
                    </div>
                    {viewMode === 'tiles' && (
                        <div className="header-ticker" style={{ display: 'flex', alignItems: 'baseline', borderLeft: '1px solid #444' }}>
                            <HeaderMetrics 
                                totals={buildGroupTotals([...(sortedProperties || []), ...(sortedVehicles || [])])} 
                                groupAssets={[...(sortedProperties || []), ...(sortedVehicles || [])]} 
                                isTicker={true}
                            />
                        </div>
                    )}
                    <div className="header-breadcrumb-container" style={{ flexShrink: 0, marginLeft: 'auto' }}>
                        <Link to="/balance_sheet/assets/" style={{ whiteSpace: 'nowrap' }}> All Assets</Link>
                    </div>
                </div>
                {/* Tabs row with Add button aligned to right; match tiles container width/padding for right-edge alignment */}
                <div
                    className="tabs-row"
                    style={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                        marginTop: 12,
                        width: '90%',
                        padding: '0 20px',
                        boxSizing: 'border-box'
                    }}
                >
                    <div className="view-tabs" style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                        <div className="tab-bar">
                            <button
                                className={`tab-btn ${viewMode === 'tiles' ? 'active' : ''}`}
                                onClick={() => setViewMode('tiles')}
                            >Tiles</button>
                            <button
                                className={`tab-btn ${viewMode === 'table' ? 'active' : ''}`}
                                onClick={() => setViewMode('table')}
                            >Table</button>
                        </div>

                        {viewMode === 'tiles' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <div style={{ display: 'flex', gap: 4 }}>
                                    {['1M', '3M', '6M', '12M', 'YTD', 'ALL'].map(p => (
                                        <button
                                            key={p}
                                            className={`btn btn-sm ${period === p ? 'btn-primary' : 'btn-light'}`}
                                            onClick={() => setPeriod(p)}
                                            style={{ fontSize: '14px', padding: '4px 8px', height: '32px', minWidth: '44px' }}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                </div>
                                <div className="btn-group">
                                    {['$', '%'].map(m => (
                                        <button
                                            key={m}
                                            className={`btn btn-sm ${deltaMode === m ? 'btn-primary' : 'btn-light'}`}
                                            onClick={() => setDeltaMode(m)}
                                            style={{ fontSize: '14px', padding: '4px 10px', height: '32px' }}
                                        >
                                            {m}
                                        </button>
                                    ))}
                                </div>

                                <Link to="create-physical/">
                                    <button type="button" className="btn btn-primary" style={{ fontSize: '14px', height: '32px', padding: '0 12px', display: 'flex', alignItems: 'center' }}>
                                        Add New Physical Asset
                                    </button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {viewMode === 'table' && (
            <table className="physical-assets-table">
                <thead>
                    <tr>
                        <th>Asset ID</th>
                        <th>Asset Type</th>
                        <th>Description</th>
                        <th>Purchase Date</th>
                        <th>Purchase Price</th>
                        <th>Initial Equity Financing</th>
                        <th>Initial Debt Financing</th>
                        <th>Initial D/E Ratio</th>
                        <th>Curr. Equity (BV) </th>
                        <th>Curr. Outstanding Debt</th>
                        <th>Curr. D/E Ratio</th>
                        <th>TCO (to-date)</th>
                    </tr>
                </thead>
                <tbody>
                    {categorizedAssets.residentialProperties.map(resiProperty => (
                        <React.Fragment key={resiProperty.id}>
                            <tr>
                                <td>
                                    <Link to={`/balance_sheet/assets/physical_assets/resi_properties/${resiProperty.id}`}>
                                        {resiProperty.id}
                                    </Link>
                                </td>
                                <td>
                                    <Link to={`/balance_sheet/assets/physical_assets/resi_properties`}>
                                        {resiProperty.asset_type_2}
                                    </Link>
                                </td>
                                <td>{resiProperty.description}</td>
                                <td>{resiProperty.initial_purchase_price.payment_date}</td>
                                <td>{formatCurrency(resiProperty.initial_purchase_price.amount, 0)}</td>
                                <td>{formatCurrency(resiProperty.financing.total_equity_amount, 0)}</td>
                                <td>{formatCurrency(resiProperty.financing.total_debt_amount, 0)}</td>
                                <td>{formatRate(resiProperty.financing.debt_to_equity_ratio, 1)}</td>
                                <td>{formatCurrency(resiProperty.current_bv_equity, 0)}</td>
                                <td>{formatCurrency(resiProperty.current_outstanding_debt, 0)}</td>
                                <td>{formatRate(resiProperty.current_bv_debt_to_equity, 1)}</td>
                <td style={{ color: '#fbbf24' }}>
                                    {(() => {
                                        const val = tcoCache[resiProperty.id]?.[period];
                                        if (val === undefined) return '...';
                                        if (period === 'YTD') {
                                            const avg = val / currentMonthNum;
                                            return `${formatCurrency(val, 0)} / ${formatCurrency(avg, 0)}`;
                                        }
                                        return formatCurrency(val, 0);
                                    })()}
                                </td>
                            </tr>
                        </React.Fragment>
                    ))}
                    {categorizedAssets.vehicles.map(vehicle => (
                        <React.Fragment key={vehicle.id}>
                            <tr>
                                <td>
                                    <Link to={`/balance_sheet/assets/physical_assets/vehicles/${vehicle.id}`}>
                                        {vehicle.id}
                                    </Link>
                                </td>
                                <td>
                                    <Link to={`/balance_sheet/assets/physical_assets/vehicles`}>
                                        {vehicle.asset_type_2}
                                    </Link>
                                </td>
                                <td>{vehicle.description}</td>
                                <td>{vehicle.initial_purchase_price.payment_date}</td>
                                <td>{formatCurrency(vehicle.initial_purchase_price.amount, 0)}</td>
                                <td>{formatCurrency(vehicle.financing.total_equity_amount, 0)}</td>
                                <td>{formatCurrency(vehicle.financing.total_debt_amount, 0)}</td>
                                <td>{formatRate(vehicle.financing.debt_to_equity_ratio, 1)}</td>
                                <td>{formatCurrency(vehicle.current_bv_equity, 0)}</td>
                                <td>{formatCurrency(vehicle.current_outstanding_debt, 0)}</td>
                                <td>{formatRate(vehicle.current_bv_debt_to_equity, 1)}</td>
                                <td style={{ color: '#fbbf24' }}>
                                    {(() => {
                                        const val = tcoCache[vehicle.id]?.[period];
                                        if (val === undefined) return '...';
                                        if (period === 'YTD') {
                                            const avg = val / currentMonthNum;
                                            return `${formatCurrency(val, 0)} / ${formatCurrency(avg, 0)}`;
                                        }
                                        return formatCurrency(val, 0);
                                    })()}
                                </td>
                            </tr>
                        </React.Fragment>
                    ))}
                    {/* Total row */}
                    <tr className="total-row">
                        <td></td>
                        <td><strong>Total</strong></td>
                        <td></td>
                        <td></td>
                        <td><strong>{formatCurrency(totalFields.initialPurchasePrice, 0)}</strong></td>
                        <td><strong>{formatCurrency(totalFields.totalEquity, 0)}</strong></td>
                        <td><strong>{formatCurrency(totalFields.totalDebt, 0)}</strong></td>
                        <td><strong>{formatRate(totalFields.totalDebt/totalFields.totalEquity, 1)}</strong></td>
                        <td><strong>{formatCurrency(totalFields.totalCurrentBvEquity, 0)}</strong></td>
                        <td><strong>{formatCurrency(totalFields.totalCurrentOsDebt, 0)}</strong></td>
                        <td><strong>{formatRate(totalFields.totalCurrentOsDebt/totalFields.totalCurrentBvEquity, 1)}</strong></td>
                        <td>
                            <strong style={{ color: '#fbbf24' }}>
                                {(() => {
                                    const val = totalFields.totalTCO;
                                    if (period === 'YTD') {
                                        const avg = val / currentMonthNum;
                                        return `${formatCurrency(val, 0)} / ${formatCurrency(avg, 0)}`;
                                    }
                                    return formatCurrency(val, 0);
                                })()}
                            </strong>
                        </td>
                    </tr>
                    {physicalAssets.length === 0 && (
                        <tr>
                            <td colSpan="3" style={{ textAlign: 'center' }}>No physical assets found.</td>
                        </tr>
                    )}
                </tbody>
            </table>
            )}
            {viewMode === 'tiles' && (
                <div style={{ width: '90%', padding: '0 20px 40px 20px', ...(typeof titleColWidth === 'number' ? { '--title-col-width': `${titleColWidth}px` } : {}) }}>
                    {/* Properties section header with inline aggregates */}
                    <div className="tiles-section-header">
                        <div className="header-title-wrap">
                            <button
                                type="button"
                                className="collapse-btn"
                                aria-expanded={propertiesExpanded}
                                aria-controls="properties-grid"
                                title={(propertiesExpanded ? 'Collapse' : 'Expand') + ' Properties tiles'}
                                onClick={() => setPropertiesExpanded(prev => !prev)}
                            >{propertiesExpanded ? '−' : '+'}</button>
                            <h2 ref={propertiesTitleRef} className="tiles-section-title" style={{ margin: 0 }}>All Properties</h2>
                        </div>
                        <HeaderMetrics totals={buildGroupTotals(sortedProperties)} groupAssets={sortedProperties} />
                    </div>
                    {propertiesExpanded && (
                    <div className="tiles-grid" id="properties-grid">
                        {sortedProperties.map(asset => (
                            <Link
                                key={`resi_property-${asset.id}`}
                                to={`/balance_sheet/assets/physical_assets/resi_properties/${asset.id}`}
                                className="tile-card-link"
                            >
                                <div className="tile-card">
                                    {asset.photo_thumb ? (
                                        <img src={asset.photo_thumb} alt="Property" className="tile-img" loading="lazy" />
                                    ) : (
                                        <div className="tile-icon" aria-hidden>{renderIcon({ ...asset, _tileType: 'resi_property' })}</div>
                                    )}
                                    <div className="tile-title">{asset.description || `Property #${asset.id}`}</div>
                                    <div className="tile-subtitle">{asset.initial_purchase_price?.payment_date || ''}</div>
                                    <div className="tile-metrics">
                                        <div>
                                            <span className="metric-label">Current Value</span>
                                            <span className="metric-value">
                                                {formatCurrency(getCurrentValue(asset) || 0, 0)}
                                                {(() => {
                                                    const dValue = getDelta(asset.id, 'value');
                                                    if (!dValue) return null;
                                                    return <span style={{ fontSize: '0.85em', marginLeft: 4, color: getDeltaColor(dValue, 'value') }}>({dValue})</span>;
                                                })()}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="metric-label">Purchase Amount</span>
                                            <span className="metric-value">{formatCurrency(asset.initial_purchase_price?.amount || 0, 0)}</span>
                                        </div>
                                        <div>
                                            <span className="metric-label">Current Equity</span>
                                            {(() => {
                                                const currentVal = getCurrentValue(asset) || 0;
                                                const osDebt = sumOutstandingDebt(asset) || 0;
                                                const eq = currentVal - osDebt;
                                                const cls = eq >= 0 ? 'metric-value positive' : 'metric-value negative';
                                                const dEquity = getDelta(asset.id, 'equity');
                                                return (
                                                    <span className={cls}>
                                                        {formatCurrency(eq, 0)}
                                                        {dEquity && <span style={{ fontSize: '0.85em', marginLeft: 4, color: getDeltaColor(dEquity, 'equity') }}>({dEquity})</span>}
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                        <div className="debt-metric" onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); onToggleDebtTip(`resi_property-${asset.id}`, asset); }}>
                                            <span className="metric-label">Outstanding Debt</span>
                                            <span className="metric-value debt-amount-link" onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); onToggleDebtTip(`resi_property-${asset.id}`, asset); }}>
                                                {formatCurrency(sumOutstandingDebt(asset), 0)}
                                                {(() => {
                                                    const dDebt = getDelta(asset.id, 'debt');
                                                    if (!dDebt) return null;
                                                    return <span style={{ fontSize: '0.85em', marginLeft: 4, color: getDeltaColor(dDebt, 'debt') }}>({dDebt})</span>;
                                                })()}
                                            </span>
                                            {openDebtTip.assetKey === `resi_property-${asset.id}` && (
                                                <div className="debt-tooltip" onClick={(e)=> e.stopPropagation()}>
                                                    {(() => {
                                                        const rows = buildDebtBreakdown(asset);
                                                        if (!rows.length) return <div className="debt-tip-row empty">No breakdown available</div>;
                                                        return rows.map((r, i) => (
                                                            <div key={i} className="debt-tip-row">
                                                                <span className="debt-tip-label">{r.label}</span>
                                                                <a className="debt-tip-amount" href={r.href} onClick={(e)=> e.stopPropagation()}>
                                                                    {(() => {
                                                                        const cacheKey = r.key || (r.id != null ? String(r.id) : null);
                                                                        const cached = cacheKey ? debtAmtCache[cacheKey] : undefined;
                                                                        const val = (typeof cached === 'number' && !isNaN(cached))
                                                                            ? cached
                                                                            : (typeof r.amount === 'number' && !isNaN(r.amount) ? r.amount : null);
                                                                        return (val != null) ? formatCurrency(val, 0) : '…';
                                                                    })()}
                                                                </a>
                                                            </div>
                                                        ));
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <span className="metric-label">TCO ({period})</span>
                                            <span className="metric-value" style={{ color: '#fbbf24' }}>
                                                {(() => {
                                                    const val = tcoCache[asset.id]?.[period];
                                                    if (val === undefined) return '...';
                                                    if (period === 'YTD') {
                                                        const avg = val / currentMonthNum;
                                                        return `${formatCurrency(val, 0)} / ${formatCurrency(avg, 0)}`;
                                                    }
                                                    return formatCurrency(val, 0);
                                                })()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                        {sortedProperties.length === 0 && (
                            <div className="tiles-empty">No properties found.</div>
                        )}
                    </div>
                    )}

                    {/* Vehicles section header with inline aggregates */}
                    <div className="tiles-section-header" style={{ marginTop: 24 }}>
                        <div className="header-title-wrap">
                            <button
                                type="button"
                                className="collapse-btn"
                                aria-expanded={vehiclesExpanded}
                                aria-controls="vehicles-grid"
                                title={(vehiclesExpanded ? 'Collapse' : 'Expand') + ' Vehicles tiles'}
                                onClick={() => setVehiclesExpanded(prev => !prev)}
                            >{vehiclesExpanded ? '−' : '+'}</button>
                            <h2 ref={vehiclesTitleRef} className="tiles-section-title" style={{ margin: 0 }}>All Vehicles</h2>
                        </div>
                        <HeaderMetrics totals={buildGroupTotals(sortedVehicles)} groupAssets={sortedVehicles} />
                    </div>
                    {vehiclesExpanded && (
                    <div className="tiles-grid" id="vehicles-grid">
                        {sortedVehicles.map(asset => (
                            <Link
                                key={`vehicle-${asset.id}`}
                                to={`/balance_sheet/assets/physical_assets/vehicles/${asset.id}`}
                                className="tile-card-link"
                            >
                                <div className="tile-card">
                                    {asset.photo_thumb ? (
                                        <img src={asset.photo_thumb} alt="Vehicle" className="tile-img" loading="lazy" />
                                    ) : (
                                        <div className="tile-icon" aria-hidden>{renderIcon({ ...asset, _tileType: 'vehicle' })}</div>
                                    )}
                                    <div className="tile-title">{asset.description || `Vehicle #${asset.id}`}</div>
                                    <div className="tile-subtitle">{asset.initial_purchase_price?.payment_date || ''}</div>
                                    <div className="tile-metrics">
                                        <div>
                                            <span className="metric-label">Current Value</span>
                                            <span className="metric-value">
                                                {formatCurrency(getCurrentValue(asset) || 0, 0)}
                                                {(() => {
                                                    const dValue = getDelta(asset.id, 'value');
                                                    if (!dValue) return null;
                                                    return <span style={{ fontSize: '0.85em', marginLeft: 4, color: getDeltaColor(dValue, 'value') }}>({dValue})</span>;
                                                })()}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="metric-label">Purchase Amount</span>
                                            <span className="metric-value">{formatCurrency(asset.initial_purchase_price?.amount || 0, 0)}</span>
                                        </div>
                                        <div>
                                            <span className="metric-label">Current Equity</span>
                                            {(() => {
                                                const currentVal = getCurrentValue(asset) || 0;
                                                const osDebt = sumOutstandingDebt(asset) || 0;
                                                const eq = currentVal - osDebt;
                                                const cls = eq >= 0 ? 'metric-value positive' : 'metric-value negative';
                                                const dEquity = getDelta(asset.id, 'equity');
                                                return (
                                                    <span className={cls}>
                                                        {formatCurrency(eq, 0)}
                                                        {dEquity && <span style={{ fontSize: '0.85em', marginLeft: 4, color: getDeltaColor(dEquity, 'equity') }}>({dEquity})</span>}
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                        <div className="debt-metric" onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); onToggleDebtTip(`vehicle-${asset.id}`, asset); }}>
                                            <span className="metric-label">Outstanding Debt</span>
                                            <span className="metric-value debt-amount-link" onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); onToggleDebtTip(`vehicle-${asset.id}`, asset); }}>
                                                {formatCurrency(sumOutstandingDebt(asset), 0)}
                                                {(() => {
                                                    const dDebt = getDelta(asset.id, 'debt');
                                                    if (!dDebt) return null;
                                                    return <span style={{ fontSize: '0.85em', marginLeft: 4, color: getDeltaColor(dDebt, 'debt') }}>({dDebt})</span>;
                                                })()}
                                            </span>
                                            {openDebtTip.assetKey === `vehicle-${asset.id}` && (
                                                <div className="debt-tooltip" onClick={(e)=> e.stopPropagation()}>
                                                    {(() => {
                                                        const rows = buildDebtBreakdown(asset);
                                                        if (!rows.length) return <div className="debt-tip-row empty">No breakdown available</div>;
                                                        return rows.map((r, i) => (
                                                            <div key={i} className="debt-tip-row">
                                                                <span className="debt-tip-label">{r.label}</span>
                                                                <a className="debt-tip-amount" href={r.href} onClick={(e)=> e.stopPropagation()}>
                                                                    {(() => {
                                                                        const cacheKey = r.key || (r.id != null ? String(r.id) : null);
                                                                        const cached = cacheKey ? debtAmtCache[cacheKey] : undefined;
                                                                        const val = (typeof cached === 'number' && !isNaN(cached))
                                                                            ? cached
                                                                            : (typeof r.amount === 'number' && !isNaN(r.amount) ? r.amount : null);
                                                                        return (val != null) ? formatCurrency(val, 0) : '…';
                                                                    })()}
                                                                </a>
                                                            </div>
                                                        ));
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <span className="metric-label">TCO ({period})</span>
                                            <span className="metric-value" style={{ color: '#fbbf24' }}>
                                                {(() => {
                                                    const val = tcoCache[asset.id]?.[period];
                                                    if (val === undefined) return '...';
                                                    if (period === 'YTD') {
                                                        const avg = val / currentMonthNum;
                                                        return `${formatCurrency(val, 0)} / ${formatCurrency(avg, 0)}`;
                                                    }
                                                    return formatCurrency(val, 0);
                                                })()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                        {sortedVehicles.length === 0 && (
                            <div className="tiles-empty">No vehicles found.</div>
                        )}
                    </div>
                    )}
                </div>
            )}
            <style jsx>{`
                .physical-assets-table {
                    margin: 20px;
                    width: 90%;
                    table-layout: auto;
                    border-collapse: collapse;
                }
                .physical-assets-table th, .physical-assets-table td {
                    border: 1px solid #ccc;
                    padding: 10px;
                    text-align: left;
                }
                .physical-assets-table th {
                    background-color: #f0f0f0;
                }
                .total-row td {
                    border: none; /* Remove borders for the total row */
                }
                /* Notebook-style tabs (mirror dark theme like property pages) */
                .tab-bar {
                    display: inline-flex;
                    align-items: flex-end;
                    gap: 4px;
                    border-bottom: 1px solid #374151;
                }
                .view-tabs .tab-btn {
                    background: #111827;
                    color: #e5e7eb;
                    border: 1px solid #374151;
                    border-bottom-color: #374151;
                    padding: 6px 14px;
                    border-top-left-radius: 8px;
                    border-top-right-radius: 8px;
                    cursor: pointer;
                    font-size: 14px;
                }
                .view-tabs .tab-btn:hover { background: #0f172a; }
                .view-tabs .tab-btn.active {
                    background: #0b1220;
                    border-color: #4b5563;
                    border-bottom-color: transparent; /* lift active tab above bar */
                    color: #ffffff;
                    position: relative;
                    top: 1px;
                }
                .tiles-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
                    gap: 20px;
                    padding: 18px 0 10px 0;
                }
                .tile-card-link {
                    text-decoration: none;
                    color: inherit;
                }
                .tile-card {
                    border: 1px solid #2a2a2a;
                    border-radius: 10px;
                    padding: 18px;
                    background: #1e1e1e;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.3);
                    transition: box-shadow 0.15s ease, transform 0.15s ease;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }
                .tile-card:hover {
                    box-shadow: 0 6px 18px rgba(0,0,0,0.35);
                    transform: translateY(-2px);
                }
                .tile-icon {
                    font-size: 48px;
                    line-height: 1;
                }
                .tile-img {
                    width: 100%;
                    height: 200px;
                    object-fit: cover;
                    border-radius: 8px;
                }
                .tile-title {
                    font-weight: 700;
                    margin-top: 12px;
                    font-size: 18px;
                    color: #e6e6e6;
                }
                .tile-subtitle {
                    color: #aaaaaa;
                    font-size: 14px;
                    margin-top: 2px;
                }
                .tile-metrics {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 10px 14px;
                    margin-top: 12px;
                    font-size: 15px;
                }
                .metric-label {
                    display: block;
                    color: #bbbbbb;
                }
                .metric-value {
                    display: block;
                    font-weight: 600;
                    color: #ffffff;
                }
                .metric-value.positive { color: #34d399; }
                .metric-value.negative { color: #f87171; }
                /* Debt link + tooltip */
                .debt-metric { position: relative; }
                .debt-amount-link { cursor: pointer; color: #93c5fd; }
                .debt-amount-link:hover { text-decoration: underline; }
                .debt-tooltip {
                    position: absolute;
                    z-index: 30;
                    top: 100%;
                    right: 0;
                    margin-top: 6px;
                    min-width: 220px;
                    max-width: 320px;
                    background: #0b1220;
                    border: 1px solid #374151;
                    border-radius: 8px;
                    box-shadow: 0 12px 24px rgba(0,0,0,0.4);
                    padding: 8px;
                }
                .debt-tip-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 6px 4px; }
                .debt-tip-row + .debt-tip-row { border-top: 1px solid #1f2937; }
                .debt-tip-row.empty { color: #9ca3af; justify-content: center; }
                .debt-tip-label { color: #e5e7eb; font-size: 15px; }
                .debt-tip-amount { color: #93c5fd; text-decoration: none; font-weight: 600; }
                .debt-tip-amount:hover { text-decoration: underline; }
                /* Section headers: title + inline totals with full-width divider */
                .header-metrics {
                    font-size: 18px;
                }
                .header-metric {
                    text-align: right;
                }
                .header-metric .metric-label {
                    display: block;
                    font-size: 13px;
                    color: #bbbbbb;
                    margin-bottom: 2px;
                }
                .header-metric .metric-value {
                    display: block;
                    font-size: 18px;
                    font-weight: 600;
                }
                .assets-page {
                    margin-left: clamp(20px, 4vw, 40px);
                }
                .assets-page-content {
                    margin: clamp(10px, 2vw, 20px);
                }
                .assets-page h1 {
                    font-size: clamp(14px, 3.5vw, 44px) !important;
                }
                .header-title-container {
                    display: flex;
                    align-items: center;
                    margin-right: clamp(4px, 1.5vw, 24px);
                    flex-shrink: 1;
                    min-width: 0;
                }
                .header-breadcrumb-container {
                    margin-left: auto;
                    padding-left: clamp(4px, 1.5vw, 24px);
                    flex-shrink: 0;
                    min-width: 0;
                }
                .header-breadcrumb-container a {
                    font-size: clamp(10px, 1.2vw, 18px);
                    text-decoration: none;
                }
                .header-metrics.ticker {
                    display: flex !important;
                    gap: clamp(2px, 1vw, 24px);
                    align-items: baseline;
                    flex-wrap: nowrap;
                    white-space: nowrap;
                    flex-shrink: 0;
                }
                .header-metrics.ticker .header-metric {
                    text-align: left;
                    flex-shrink: 1;
                }
                .header-metrics.ticker .metric-label {
                    display: inline;
                    font-size: clamp(7px, 1.2vw, 18px);
                    margin-bottom: 0;
                }
                .header-metrics.ticker .metric-value {
                    display: inline;
                    font-size: clamp(9px, 1.8vw, 24px);
                }
                .header-ticker {
                    padding-left: clamp(4px, 1.5vw, 24px);
                    margin-left: clamp(2px, 0.8vw, 12px);
                    transition: all 0.2s ease;
                    flex-shrink: 1;
                    min-width: 0;
                    overflow: visible;
                }
                @media (max-width: 1200px) {
                    .header-ticker { padding-left: clamp(2px, 0.5vw, 8px) !important; margin-left: clamp(1px, 0.3vw, 4px) !important; }
                }
                @media (max-width: 900px) {
                    .header-ticker { border-left: none !important; padding-left: 0 !important; margin-left: 0 !important; }
                    .assets-page { margin-left: 10px; }
                    .assets-page-content { margin: 5px; }
                }
                .tiles-section-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 24px;
                    width: 100%;
                    box-sizing: border-box;
                    border-bottom: 1px solid #2a2a2a;
                    padding-bottom: 6px;
                }
                .tiles-section-spacer { flex: 1; }
                .tiles-section-title {
                    font-size: 22px;
                    font-weight: 600;
                    color: #dddddd;
                    margin: 8px 0 8px 0;
                    /* Remove positional nudge; rely on flex centering for clean alignment */
                    min-width: var(--title-col-width, auto); /* align metrics start x-position across headers */
                }
                .header-title-wrap { display: inline-flex; align-items: center; gap: 8px; }
                .collapse-btn {
                    width: 20px; height: 20px; padding: 0; line-height: 18px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: #111827; color: #e5e7eb; border: 1px solid #374151;
                    border-radius: 4px; cursor: pointer; font-weight: 700; user-select: none;
                }
                .collapse-btn:hover { background: #0f172a; }
                .collapse-btn:focus { outline: 2px solid #2563eb; outline-offset: 1px; }
                .tiles-empty {
                    color: #aaaaaa;
                    padding: 8px 0 16px 0;
                }
            `}</style>
        </div>
    );
};

export default PhysicalAssetsPage;